/**
 * Logger utility that automatically disables console logging in production
 *
 * Usage:
 *   import { logger } from '@/models/logger'
 *   logger.log('message')
 *   logger.info('info message')
 *   logger.warn('warning message')
 *   logger.error('error message')
 *   logger.debug('debug message')
 */

class Logger {
  private isProduction: boolean
  private enabledInProduction: Set<string>

  constructor() {
    this.isProduction = process.env.NODE_ENV === 'production'
    // Errors and warnings are always enabled, even in production
    this.enabledInProduction = new Set(['error', 'warn'])
  }

  /**
   * Standard log - disabled in production
   */
  log(...args: any[]): void {
    if (!this.isProduction) {
      console.log(...args)
    }
  }

  /**
   * Info log - disabled in production
   */
  info(...args: any[]): void {
    if (!this.isProduction) {
      console.info(...args)
    }
  }

  /**
   * Debug log - disabled in production
   */
  debug(...args: any[]): void {
    if (!this.isProduction) {
      console.debug(...args)
    }
  }

  /**
   * Warning log - enabled in production
   */
  warn(...args: any[]): void {
    console.warn(...args)
  }

  /**
   * Error log - enabled in production
   */
  error(...args: any[]): void {
    console.error(...args)
  }

  /**
   * Table log - disabled in production
   */
  table(data: any): void {
    if (!this.isProduction) {
      console.table(data)
    }
  }

  /**
   * Group log - disabled in production
   */
  group(...args: any[]): void {
    if (!this.isProduction) {
      console.group(...args)
    }
  }

  /**
   * Group end - disabled in production
   */
  groupEnd(): void {
    if (!this.isProduction) {
      console.groupEnd()
    }
  }
}

// Export singleton instance
export const logger = new Logger()

// Export default for convenience
export default logger